import { useAuth } from '@opendoor/auth-fe';
import { Entry } from 'contentful';

import { useReturnExperienceContext } from 'helpers/ReturnExperienceContext';

import { Awaited, EntryComponent, LoaderFn } from '../../cms/entries/entries';
import { ILandingPageFields, ILpComponentHeroV2WithMarkers } from '../../declarations/contentful';
import NewSellerFlowAddressInput from '../shared/NewSellerFlowAddressInput';
import { PictureSourceSpec } from '../shared/performance/pictures';
import { RenderHeroV2 } from './HeroV2';
import { ReturnExperienceHero } from './ReturnExperience';
import HeroMarkers from './shared/hero/HeroMarkers';
import { getStickyCTAFromRoot, preloadHeroImages } from './shared/hero/HeroShared';
import { HeroV2CustomizationProps } from './shared/hero/heroTypes';

const RenderHeroV2WithMarkers = (
  entry: ILpComponentHeroV2WithMarkers,
  resolvedData?: Awaited<ReturnType<typeof heroV2WithMarkersLoader>>,
) => {
  const { fields } = entry;
  const { authentication } = useAuth();
  const { showReturnExperience } = useReturnExperienceContext();

  const addressInputCommonProps = {
    hideLabel: true,
    inputLocation: 'hero-cta' as const,
    analyticsPrefix: `cosmos-landing-page-hero-v2-with-markers${
      fields?.analyticsId ? `-${fields?.analyticsId}` : ''
    }`,
    trackingTaxonomy: 'new_homepage' as const,
    trackingKeywords: fields?.trackingKeyword,
  };

  // Convert resolvedData to match HeroV2's expected format
  const heroV2ResolvedData = resolvedData
    ? { ...resolvedData, stickyCTA: resolvedData.stickyCTA || undefined }
    : undefined;

  const customization: HeroV2CustomizationProps = {
    variant: 'withMarkers',
    hideStickyCtaOverride:
      entry.sys.locale === 'es' ||
      resolvedData?.stickyCTA === 'None' ||
      authentication.state === 'authenticated',
    renderDesktopInput: () => (
      <NewSellerFlowAddressInput
        {...addressInputCommonProps}
        placeholder={fields.addressCtaPlaceholderText || 'Enter your home address'}
      />
    ),
    renderMobileInput: () => (
      <NewSellerFlowAddressInput
        {...addressInputCommonProps}
        placeholder={fields.addressCtaPlaceholderTextSmall || 'Enter an address'}
      />
    ),
    additionalAddressContent:
      showReturnExperience &&
      entry.sys.locale !== 'es' &&
      !(fields?.analyticsId === 'syw-address-entry-direct-mail') ? (
        <ReturnExperienceHero />
      ) : undefined,
    imageContainerStyles: {
      $largerThanMD: { width: '50%' as const, justifyContent: 'flex-end' as const },
    },
    desktopPressBoxBottom: '$15x' as const,
    headline: fields?.title || '',
    analyticsName: `cosmos-landing-page-hero-v2-with-markers-${fields?.analyticsId}`,
    children: (
      <HeroMarkers
        cashOfferMarkerImage={fields.cashOfferMarkerImage}
        newOfferMarkerImage={fields.newOfferMarkerImage}
        highestOfferMarkerImage={fields.highestOfferMarkerImage}
      />
    ),
  };

  return RenderHeroV2(entry, heroV2ResolvedData, customization);
};

type IHeroV2WithMarkersLoaderReturn = {
  preloadImages: PictureSourceSpec;
  stickyCTA: string | undefined | null;
};

const heroV2WithMarkersLoader: LoaderFn<
  ILpComponentHeroV2WithMarkers,
  IHeroV2WithMarkersLoaderReturn
> = async (input, root?: Entry<ILandingPageFields>) => {
  const preloadImages = preloadHeroImages({
    imageDesktopUrl: input.fields.imageDesktopUrl,
    imageMobileUrl: input.fields.imageMobileUrl,
  });

  const loaderReturn: IHeroV2WithMarkersLoaderReturn = {
    preloadImages,
    stickyCTA: getStickyCTAFromRoot(root) || null,
  };

  return loaderReturn;
};

const HeroV2WithMarkers: EntryComponent<
  ILpComponentHeroV2WithMarkers,
  Awaited<ReturnType<typeof heroV2WithMarkersLoader>>
> = {
  render: RenderHeroV2WithMarkers,
  loader: heroV2WithMarkersLoader,
};

export default HeroV2WithMarkers;
